<template>
  <div class="volunteeringDetail page-content pb-6">
    <v-parallax
      src="@/assets/header-parallax-2.jpg"
      height="200"
      class="page-header"
    >
      <v-container>
        <h1>{{ $t("texts.volunteeringName") }} {{ data.name }}</h1>
      </v-container>
    </v-parallax>

    <v-container>
      <v-row class="applyBtns">
        <v-col class="text-left">
          <v-btn class="primary" @click="$router.back()">
            <v-icon left small>mdi-triangle mdi-rotate-270</v-icon>

            {{ $t("texts.goback") }}
          </v-btn>
        </v-col>

        <v-col class="text-right" v-if="canApply">
          <v-btn class="primary" @click="applyDialog = true">
            {{ $t("texts.volunteeringApply") }}
          </v-btn>
        </v-col>
      </v-row>

      <v-card
        color="grey lighten-5"
        style="max-width: 85%"
        elevation="4"
        class="mx-auto"
      >
        <volunteering-detail-card :data="data"></volunteering-detail-card>
      </v-card>
    </v-container>
    <!-- Apply Info Item Dialog -->
    <v-dialog v-model="applyDialog" persistent max-width="940px">
      <v-card>
        <v-container class="pa-4">
          <div class="d-flex flex-column-reverse flex-sm-row mx-2 mb-4">
            <div>
              <h4>{{ $t("texts.applyFor") }} {{ data.name }}</h4>
            </div>
            <div class="ml-auto">
              <v-btn icon color="accent" @click="closeApplyDialog">
                <v-icon x-large>mdi-close</v-icon>
              </v-btn>
            </div>
          </div>

          <v-row>
            <v-col cols="12">
              <v-stepper v-model="formStep">
                <v-stepper-header>
                  <v-stepper-step :complete="formStep > 1" step="1">{{
                    $t("texts.selectShifts")
                  }}</v-stepper-step>
                  <v-divider></v-divider>
                  <v-stepper-step :complete="formStep > 2" step="2">{{
                    $t("texts.selectTeeShirt")
                  }}</v-stepper-step>
                  <v-divider></v-divider>
                  <v-stepper-step :complete="formStep > 3" step="3">{{
                    $t("texts.invitePeople")
                  }}</v-stepper-step>
                </v-stepper-header>
                <v-stepper-items>
                  <v-stepper-content step="1">
                    <v-data-table
                      v-model="selectedTimeSlots"
                      :headers="timeSlotsHeaders"
                      :items="data.timeSlots"
                      item-key="_id"
                      :sort-by="['date', 'startTime']"
                      :sort-desc="[false, false]"
                      group-by="date"
                      :group-desc="false"
                      :items-per-page="-1"
                      hide-default-footer
                      multi-sort
                      show-select
                      class="elevation-1"
                    >
                      <!-- Group Header -->
                      <template
                        v-slot:[`group.header`]="{
                          group,
                          items,
                          isOpen,
                          toggle
                        }"
                      >
                        <td colspan="4" class="text-start">
                          <v-btn icon small class="ma-0" @click="toggle">
                            <v-icon>
                              {{ isOpen ? "mdi-minus" : "mdi-plus" }}
                            </v-icon>
                          </v-btn>
                          {{
                            $t("texts.timeSlotsGroupHeader", {
                              date: group,
                              timeSlotsLength: items.length,
                              totalTime: timeSlotsTotalTime(items)
                            })
                          }}
                        </td>
                      </template>
                      <!-- !Group Header -->
                      <!-- Customize Columns -->
                      <template v-slot:[`item.totalTime`]="{ item }">
                        <td class="text-start">
                          {{ formatMinutes(item.totalTime) }}
                        </td>
                      </template>
                      <!-- !Customize Columns -->
                    </v-data-table>
                  </v-stepper-content>

                  <v-stepper-content step="2">
                    <v-row>
                      <v-col cols="8" class="ml-5">
                        <v-select
                          :items="teeShirts"
                          v-model="teeShirtSize"
                          :label="$t('texts.chooseTeeShirt')"
                        ></v-select>
                      </v-col>
                    </v-row>
                  </v-stepper-content>
                  <v-stepper-content step="3">
                    <v-row>
                      <v-col cols="8" class="ml-5">
                        <p class="dialog-content-title my-6">
                          {{ $t("texts.personAssociated") }}
                        </p>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="9" class="ml-5">
                        <a-text-field
                          v-model="$v.email.$model"
                          :validator="$v.email"
                          :label="$t('texts.personEmail')"
                        >
                        </a-text-field>
                      </v-col>
                      <v-col cols="2">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              fab
                              small
                              dark
                              color="primary"
                              class="mt-2"
                              @click="addEmail"
                              v-bind="attrs"
                              v-on="on"
                            >
                              <v-icon>mdi-plus</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t("texts.addEmailTooltip") }}</span>
                        </v-tooltip>
                      </v-col>
                      <v-chip
                        v-for="(item, i) in emails"
                        :key="item"
                        class="ma-2"
                        close
                        color="primary"
                        @click:close="emails.splice(i, 1)"
                      >
                        {{ item }}
                      </v-chip>
                    </v-row>
                  </v-stepper-content>
                </v-stepper-items>
              </v-stepper>
            </v-col>
          </v-row>

          <div
            class="d-flex flex-column-reverse flex-sm-row align-center justify-end"
          >
            <div class="mt-2 mb-2 mt-sm-4 mb-sm-2 mr-sm-4">
              <v-btn large color="primary--text" @click="cancelApplyDialog">
                {{ formStep !== 1 ? $t("texts.previous") : $t("texts.cancel") }}
              </v-btn>
            </div>
            <div class="mt-4 mb-2 mt-sm-4 mb-sm-2 ml-sm-4">
              <v-btn large color="primary" class="mr-3" @click="apply">
                {{ formStep !== 3 ? $t("texts.next") : $t("texts.applying") }}
              </v-btn>
            </div>
          </div>
        </v-container>
      </v-card>
    </v-dialog>
    <!-- !Apply Info Item Dialog -->
  </div>
</template>

<style scope lang="sass">
.applyBtns
  margin-left:120px
  margin-right:120px
  margin-bottom: 40px
</style>

<script>
// @ is an alias to /src
import AppError from "@/utils/AppError.js";
import ATextField from "@/components/fields/ATextField.vue";
import VolunteeringDetailCard from "@/components/VolunteeringDetailCard.vue";

import { validationMixin } from "vuelidate";
import { email } from "vuelidate/lib/validators";

export default {
  name: "volunteeringDetail",

  components: {
    ATextField,
    VolunteeringDetailCard
  },

  mounted: function() {
    this.loadData();
  },

  mixins: [validationMixin],

  validations: {
    email: { email }
  },

  data() {
    return {
      formStep: 1,

      data: {
        candidates: []
      },
      selectedTimeSlots: [],
      teeShirtSize: null,
      emails: [],
      email: null,

      applyDialog: false
    };
  },
  created() {
    this.volunteeringId = this.$route.params.volunteeringId;
  },

  props: {
    volunteeringId: String
  },

  methods: {
    /**
     * Get volunteering details.
     * @public
     */
    loadData() {
      // Load form data
      this.$store.dispatch("Util/startLoading");
      let url = this.$config.api.route.volunteeringDetail;
      url = url.replace(":id", this.volunteeringId);
      this.$api({
        url: url,
        method: "GET"
      })
        .then(resp => {
          this.data = resp.data;
        })
        .catch(() => {
          AppError.handle(new AppError("LOAD_DATA_UNKNOWN"));
        })
        .finally(() => {
          this.$store.dispatch("Util/stopLoading");
        });
    },

    /**
     * Close apply dialog.
     * @public
     */
    closeApplyDialog() {
      this.applyDialog = false;
      this.formStep = 1;
    },

    /**
     * Cancel apply dialog.
     * @public
     */
    cancelApplyDialog() {
      if (this.formStep > 1) {
        --this.formStep;
      } else {
        this.closeApplyDialog();
      }
    },

    /**
     * Apply for volunteering.
     * @public
     */
    apply() {
      let url = this.$config.api.route.apply;

      switch (this.formStep) {
        case 1:
          if (this.selectedTimeSlots.length === 0) {
            this.$store.dispatch(
              "Alert/showError",
              this.$t("texts.selectAtLeastOneTimeSlot")
            );
            return;
          }
          ++this.formStep;
          break;
        case 2:
          if (this.teeShirtSize === null) {
            this.$store.dispatch(
              "Alert/showError",
              this.$t("texts.selectAtLeastOneTeeShirtSize")
            );
            return;
          }
          ++this.formStep;
          break;
        case 3:
          this.$store.dispatch("Util/startLoading");
          url = url.replace(":id", this.volunteeringId);
          this.$api({
            url: url,
            data: {
              timeSlots: this.selectedTimeSlots.map(timeSlot => timeSlot._id),
              referralEmails: this.emails,
              shirtSize: this.teeShirtSize
            },
            method: "POST"
          })
            .then(() => {
              this.editing = false;
              this.applyDialog = false;
              this.loadData();

              this.$store.dispatch(
                "Alert/showSuccess",
                this.$t("texts.successApply")
              );
              this.emails = null;
            })
            .catch(err => {
              AppError.handle(err);
            })
            .finally(() => {
              this.$store.dispatch("Util/stopLoading");
            });
          break;
      }
    },

    /**
     * Add person email to an apply.
     * @public
     */
    addEmail() {
      this.$v.email.$touch();
      if (this.$v.email.$invalid) {
        return;
      }

      this.emails.push(this.email);
      this.email = null;
    },
    /**
     * Return minutes formatted in "10h 30m"
     * @public
     */
    formatMinutes(minutes) {
      const hours = Math.floor(minutes / 60);

      return hours + "h " + (minutes % 60) + "m";
    },
    /**
     * Time slots total time
     * @public
     */
    timeSlotsTotalTime(timeSlots) {
      let totalMinutes = 0;

      timeSlots.forEach(timeSlot => {
        totalMinutes += timeSlot.totalTime;
      });

      return this.formatMinutes(totalMinutes);
    }
  },

  computed: {
    /**
     * Check if user has already applied for a volunteering.
     */

    teeShirts() {
      return [
        {
          value: 1,
          text: "XS"
        },
        {
          value: 2,
          text: "S"
        },
        {
          value: 3,
          text: "M"
        },
        {
          value: 4,
          text: "L"
        },
        {
          value: 5,
          text: "XL"
        }
      ];
    },
    canApply() {
      if (Object.keys(this.data).length === 0) {
        return false;
      }

      if (!this.$store.getters["Auth/isAuthenticated"]) {
        return false;
      }

      if (this.$store.getters["Auth/getUser"]["type"] !== 1) {
        return false;
      }

      if (
        !this.data.dates ||
        new Date(this.data.dates.applicationsEnd) < new Date()
      ) {
        return false;
      }

      if (!this.data.candidates || this.data.candidates.length === 0) {
        return true;
      }

      return (
        this.data.candidates.find(
          item => item.userId === this.$store.getters["Auth/getUser"]["id"]
        ) === undefined
      );
    },

    /**
     * Time slots datatable headers
     */
    timeSlotsHeaders() {
      return [
        {
          text: this.$t("texts.date"),
          value: "date"
        },
        {
          text: this.$t("texts.startTimeSlot"),
          value: "startTime",
          groupable: false
        },
        {
          text: this.$t("texts.endTimeSlot"),
          value: "endTime",
          groupable: false
        },
        {
          text: this.$t("texts.totalTimeSlot"),
          value: "totalTime",
          groupable: false
        }
      ];
    }
  }
};
</script>
